import React, { useState, useEffect, FC } from 'react';
import { Tooltip } from '@mui/material';
import images from '../../../Utils/images';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { styledSx } from '../../ContactModule/Tags/variableDependencies';
import { workflowHistory, workflowAutomationQuota } from '../../../Services/index';
import { useSelector } from 'react-redux';
import { formatDate } from '../../../Utils/localdate';

interface QuotaCardProps {
  count: number;
  label: string;
  tooltipText?: string;
  warning?: boolean;
}

interface QuotaData {
  remainingQuota: number;
  executedTasksQuota: number;
  executedTasksPaid: number;
  executedTasksFree: number;
}

interface WorkflowHistoryItem {
  id: string;
  date: string;
  name: string;
  quota: number;
  freequota: number;
  paidtasks: number;
  taskid: string;
}

interface WorkflowHistoryApiResponse {
  data: {
    data: {
      taskId: string;
      date?: string;
      workflowName?: string;
      quotaTasks?: number;
      freeTasks?: number;
      paidTasks?: number;
    }[];
    total: number;
  };
}

const QuotaCard: FC<QuotaCardProps> = ({ count, label, tooltipText, warning }) => {
  return (
    <div
      className="ticket_component_container"
      style={{
        backgroundColor: '#fff',
        borderRadius: 14,
        padding: '16px',
        flex: 1,
        boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <p className="count_style" style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{count}</p>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {warning && <span style={{ color: 'red', fontWeight: 'bold' }}>⚠</span>}
          <p className="label_style" style={{ margin: 0, fontSize: 16, color: '#555', fontWeight: 400 }}>{label}</p>
          {tooltipText && (
            <Tooltip title={tooltipText} arrow>
              <img
                style={{ height: 18, width: 18, objectFit: 'contain', cursor: 'pointer' }}
                src={images.InfoCircle}
                alt="info"
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

const WorkflowHistoryComponent: FC = () => {
  const [historyData, setHistoryData] = useState<WorkflowHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const authtoken = useSelector((state: any) => state.main.auth_token);
  const localdata = useSelector((state: any) => state.main.localdata);

  const isWalletSetupComplete = localdata?.walletCurrency === 'INR' || false;
  const [quotaData, setQuotaData] = useState<QuotaData>({
    remainingQuota: 0,
    executedTasksQuota: 0,
    executedTasksPaid: 0,
    executedTasksFree: 0
  });

  const columns: GridColDef[] = [
    { field: 'date', headerName: 'Date', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'name', headerName: 'Workflow Name', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'quota', headerName: 'Total Tasks', flex: 1, align: 'center', headerAlign: 'center' }, 
    { field: 'freequota', headerName: 'Free Tasks', flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'paidtasks', headerName: 'Paid Tasks', flex: 1, align: 'center', headerAlign: 'center' },
  ];

  useEffect(() => {
    getWorkflowAutomationQuota();
  }, []);

  useEffect(() => {
    getWorkflowHistoryApiCall();
  }, [page, rowsPerPage]);

  const getWorkflowHistoryApiCall = async () => {
    setLoading(true);
    try {
      const data = {
        limit: rowsPerPage,
        page: page,
      };

      const res: WorkflowHistoryApiResponse = await workflowHistory(data, authtoken);
      const apiRows = res?.data?.data || [];
      const rows: WorkflowHistoryItem[] = apiRows.map((item, index) => ({
        id: `${item.taskId}-${index}`,
        date: item.date ? new Date(item.date).toLocaleString() : 'N/A',
        name: item.workflowName || 'N/A',
        quota: item.quotaTasks || 0,
        freequota: item.freeTasks || 0,
        paidtasks: item.paidTasks || 0,
        taskid: item.taskId || 'N/A',
      }));

      setHistoryData(rows);
      setTotalRows(res?.data?.total || 0);
    } catch (err) {
      console.error('Error fetching workflow history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWorkflowAutomationQuota = async () => {
    try {
      const res = await workflowAutomationQuota(authtoken);
      const data = res?.data || {};

      setQuotaData({
        remainingQuota: data.remainingQuota || 0,
        executedTasksQuota: data.executedTasksQuota || 0,
        executedTasksPaid: data.executedTasksPaid || 0,
        executedTasksFree: data.executedTasksFree || 0
      });
    } catch (err) {
      console.error('Error fetching workflow automation quota:', err);
    }
  };

  return (
    <div style={{ padding: '16px',overflowY: 'auto', height: '100%' }}>
      {(!isWalletSetupComplete &&
         <div style={{ backgroundColor: '#ffecec', padding: '8px 12px', borderRadius: 14, color: '#d93025', marginBottom: 12, height: '60px', display: 'flex', alignItems: 'center' }}>
        ⚠ Your wallet setup is incomplete. Due to inactive wallet your workflows will stop automatically after exhaustion for monthly quota task.
      </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8,paddingInline: 10, marginTop:2 }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>
          Overview
        </h2>
<span style={{ fontSize: 16, color: '#555', fontWeight: 400 }}>
          {`${formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} to ${formatDate(new Date())}`}
        </span>
        </div>

      <div className="agent_dashboard_container__tickets_row" style={{ display: 'flex', gap: 12, marginBottom: 16, marginTop: '14px' }}>
        <QuotaCard count={quotaData.remainingQuota} label="Remaining Quota" tooltipText="Your available remaining quota for this month." />
        <QuotaCard count={quotaData.executedTasksQuota} label="Executed Tasks (Quota)" tooltipText="Number of tasks executed using quota credits." />
        <QuotaCard count={quotaData.executedTasksPaid} label="Executed Tasks (Paid)" tooltipText="Number of tasks executed using paid credits." />
      </div>

      <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: '16px', boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Workflow History</h3>
        <span style={{ fontSize: 16, color: '#555', fontWeight: 400 }}>
            {`${formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} to ${formatDate(new Date())}`}
          </span>
           </div>
        <p style={{ fontSize: 16, color: '#555', marginBottom: 12, fontWeight: 400 }}>
          Check out your complete workflow task history in one convenient location. Every action taken through Cheerio counts as a task, but not all tasks incur charges.
          For instance, tasks such as sending WhatsApp templates, email templates, and WhatsApp response messages are exempt from charges.{' '}
          <a
            href="https://www.cheerio.in/post/how-our-automation-task-pricing-works-a-complete-guide"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#8E53F7', textDecoration: 'none' }}
          >
            Learn more.
          </a>
        </p>

        <div style={{ overflowX: 'auto', width: '100%' }}>
          <div style={{ minWidth: 700 }}>
            <DataGrid
              rows={historyData}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[25, 50, 100]}
              paginationModel={{ page, pageSize: rowsPerPage }}
              onPaginationModelChange={(model: GridPaginationModel) => {
                setPage(model.page);
                setRowsPerPage(model.pageSize);
              }}
              disableRowSelectionOnClick
              loading={loading}
              sx={{
                ...styledSx,
                height: 400,
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto'
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowHistoryComponent;
